import api from './api';

export interface Organization {
  id: string;
  name: string;
  slug: string;
  subscriptionTier: string;
  subscriptionStatus: string;
  effectiveTier: string;
  manualTierOverride?: string | null;
  manualTierExpiresAt?: string | null;
  createdAt: string;
  _count: {
    users: number;
    memberships: number;
    players: number;
    leagues: number;
  };
}

export interface User {
  id: string;
  email: string;
  role: string;
  globalRole?: string;
  createdAt: string;
  organizationId: string;
  failedLoginAttempts?: number;
}

export interface Analytics {
  totalOrganizations: number;
  totalUsers: number;
  activeSubscriptions: number;
  monthlyRevenue: number;
}

export interface PwaInstallStats {
  totalInstalls: number;
  last30Days: number;
  bySource: { landing: number; app: number };
  recentInstalls: Array<{
    id: string;
    source: string;
    userAgent: string;
    createdAt: string;
    user: { email: string } | null;
  }>;
}

export interface ApiUsageStat {
  date: string;
  [seriesKey: string]: string | number;
}

export interface ApiUsageStatsResponse {
  stats: ApiUsageStat[];
  summary: Array<{
    provider: string;
    apiType: string;
    statusCode: number;
    count: number;
    avgResponseTime: number;
  }>;
  providers: string[];
  apiTypes: string[];
  triggers: string[];
}

export interface AppInterest {
  id: string;
  email: string;
  status: 'pending' | 'invited' | 'converted';
  invitedAt: string | null;
  convertedAt: string | null;
  organizationId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AppInterestStats {
  total: number;
  pending: number;
  invited: number;
  converted: number;
}

export interface PendingInvitation {
  id: string;
  email: string;
  role: string;
  token: string;
  expiresAt: string;
  createdAt: string;
  organization: { id: string; name: string; slug: string };
}

export const superAdminService = {
  async getAllOrganizations(): Promise<Organization[]> {
    const response = await api.get('/super-admin/organizations');
    return response.data.organizations;
  },

  async getOrganizationDetails(organizationId: string) {
    const response = await api.get(`/super-admin/organizations/${organizationId}`);
    return response.data.organization;
  },

  async getAllUsers(): Promise<User[]> {
    const response = await api.get('/super-admin/users');
    return response.data.users || response.data;
  },

  async getAnalytics(): Promise<Analytics> {
    const response = await api.get('/super-admin/analytics');
    return response.data.analytics;
  },

  async deleteOrganization(organizationId: string, confirmName: string): Promise<void> {
    await api.delete(`/super-admin/organizations/${organizationId}`, {
      data: { confirmName }
    });
  },

  async suspendOrganization(organizationId: string, reason?: string): Promise<void> {
    await api.post(`/super-admin/organizations/${organizationId}/suspend`, { reason });
  },

  async unsuspendOrganization(organizationId: string): Promise<void> {
    await api.post(`/super-admin/organizations/${organizationId}/unsuspend`);
  },

  async getPwaInstallStats(): Promise<PwaInstallStats> {
    const response = await api.get('/super-admin/pwa-installs');
    return response.data;
  },

  async createOrganization(data: {
    name: string;
    slug?: string;
    ownerEmail?: string;
    ownerRole?: 'OWNER' | 'ADMIN';
  }): Promise<{ organization: Organization; invitation?: any; message: string }> {
    const response = await api.post('/super-admin/organizations', data);
    return response.data;
  },

  async getPendingInvitations(): Promise<PendingInvitation[]> {
    const response = await api.get('/super-admin/invitations');
    return response.data.data || [];
  },

  async cancelInvitation(invitationId: string): Promise<void> {
    await api.delete(`/super-admin/invitations/${invitationId}`);
  },

  async inviteToOrganization(
    organizationId: string,
    data: { email: string; role: 'OWNER' | 'ADMIN' | 'MEMBER' }
  ): Promise<{ invitation: any; message: string }> {
    const response = await api.post(`/super-admin/organizations/${organizationId}/invite`, data);
    return response.data;
  },

  // App Interest Management
  async getAppInterests(status?: string): Promise<AppInterest[]> {
    const params = status ? { status } : {};
    const response = await api.get('/app-interest/admin', { params });
    return response.data.interests;
  },

  async getAppInterestStats(): Promise<AppInterestStats> {
    const response = await api.get('/app-interest/admin/stats');
    return response.data.stats;
  },

  async updateAppInterestStatus(
    id: string,
    data: { status: 'pending' | 'invited' | 'converted'; organizationId?: string }
  ): Promise<AppInterest> {
    const response = await api.put(`/app-interest/admin/${id}`, data);
    return response.data.interest;
  },

  async deleteAppInterest(id: string): Promise<void> {
    await api.delete(`/app-interest/admin/${id}`);
  },

  async invalidateUserTokens(userId: string): Promise<{ revokedCount: number }> {
    const response = await api.post(`/super-admin/users/${userId}/invalidate-tokens`);
    return response.data;
  },

  async invalidateAllTokens(): Promise<{ revokedCount: number }> {
    const response = await api.post('/super-admin/invalidate-all-tokens');
    return response.data;
  },

  // Tier Grant Management
  async grantTier(
    organizationId: string,
    data: {
      tier: 'FAMILY';
      duration: 'forever' | '1month' | '3months' | '6months' | '1year';
      reason: string;
    }
  ): Promise<{ success: boolean; organization: Organization; expiresAt: string | null }> {
    const response = await api.post(`/super-admin/organizations/${organizationId}/grant-tier`, data);
    return response.data;
  },

  async revokeTier(organizationId: string): Promise<{ success: boolean; organization: Organization }> {
    const response = await api.post(`/super-admin/organizations/${organizationId}/revoke-tier`);
    return response.data;
  },

  async toggleUnlimitedQuota(organizationId: string): Promise<{ success: boolean; unlimitedQuota: boolean; message: string }> {
    const response = await api.post(`/super-admin/organizations/${organizationId}/toggle-unlimited-quota`);
    return response.data;
  },

  async getTierGrants(): Promise<Array<{
    id: string;
    name: string;
    slug: string;
    subscriptionTier: string;
    manualTierOverride: string;
    manualTierExpiresAt: string | null;
    manualTierGrantedBy: string | null;
    manualTierGrantedAt: string | null;
    manualTierReason: string | null;
  }>> {
    const response = await api.get('/super-admin/tier-grants');
    return response.data.grants;
  },

  async getApiUsageStats(params?: {
    provider?: string;
    apiType?: string;
    trigger?: string;
    days?: number;
    granularity?: 'daily' | 'hourly';
    groupBy?: 'service' | 'apiType' | 'trigger' | 'statusCode';
  }): Promise<ApiUsageStatsResponse> {
    const response = await api.get('/super-admin/api-usage-stats', { params });
    return response.data;
  },

  async unlockAccount(userId: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/super-admin/users/${userId}/unlock-account`);
    return response.data;
  },

  async forcePasswordReset(userId: string, reason: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/super-admin/users/${userId}/force-password-reset`, { reason });
    return response.data;
  },
};

export interface FeatureUsageStat {
  page: string;
  count: number;
}

export interface FeatureUsageTimeSeries {
  page: string;
  date: string;
  count: number;
}

export interface FeatureUsageResponse {
  totals: FeatureUsageStat[];
  timeSeries: FeatureUsageTimeSeries[];
  days: number;
}

export async function getFeatureUsageStats(days: number = 7): Promise<FeatureUsageResponse> {
  const response = await api.get('/super-admin/feature-usage', { params: { days } });
  return response.data;
}

export async function trackPwaInstall(source: 'landing' | 'app'): Promise<void> {
  await api.post('/pwa-installs', {
    source,
    userAgent: navigator.userAgent,
  });
}
